import { NavLink, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { branchLabel } from '../lib/api';
import { useEffect, useState } from 'react';
import {
  LayoutDashboard, ShoppingCart, Package, ClipboardList, ArrowLeftRight,
  Wallet, History, BarChart3, Users, LogOut, Sun, Moon, Footprints, Loader2,
} from 'lucide-react';

const MENU = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/transaksi', label: 'Transaksi', icon: ShoppingCart },
  { to: '/stok', label: 'Stok', icon: Package },
  { to: '/opname', label: 'Opname', icon: ClipboardList },
  { to: '/oper-stok', label: 'Oper Stok', icon: ArrowLeftRight },
  { to: '/pengeluaran', label: 'Pengeluaran', icon: Wallet },
  { to: '/riwayat', label: 'Riwayat', icon: History },
  { to: '/laporan', label: 'Laporan', icon: BarChart3, admin: true },
  { to: '/pengguna', label: 'Pengguna', icon: Users, admin: true },
];

function useTheme() {
  const [theme, setTheme] = useState(() => localStorage.getItem('lodi_theme') || 'dark');
  useEffect(() => {
    document.documentElement.classList.toggle('light', theme === 'light');
    localStorage.setItem('lodi_theme', theme);
  }, [theme]);
  return [theme, setTheme];
}

export function BranchBadge({ branch }) {
  if (branch === 'all') return <span className="bg-brand/10 text-brand border border-brand/25 px-2.5 py-0.5 rounded-full text-xs font-semibold" data-testid="branch-badge">Owner</span>;
  const cls = branch === 'balaraja' ? 'badge-balaraja' : branch === 'ciledug' ? 'badge-ciledug' : 'bg-teal-500/10 text-teal-400 border border-teal-500/25 px-2.5 py-0.5 rounded-full text-xs font-semibold';
  return <span className={cls} data-testid="branch-badge">{branchLabel(branch)}</span>;
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [theme, setTheme] = useTheme();
  const items = MENU.filter((m) => !m.admin || user.role === 'admin');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navLinkCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
      isActive ? 'bg-brand text-white shadow-lg shadow-brand/25' : 'text-muted-foreground hover:text-foreground hover:bg-elevated'
    }`;

  return (
    <div className="min-h-screen bg-background" data-testid="app-layout">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-surface border-r border-line z-40">
        <div className="flex items-center gap-3 px-5 py-6">
          <div className="w-10 h-10 rounded-xl bg-brand flex items-center justify-center shadow-lg shadow-brand/30">
            <Footprints className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-display font-extrabold text-lg tracking-tight leading-none">LODISHOES</h1>
            <p className="text-[11px] text-muted-foreground mt-0.5">POS Kasir Sepatu & Sandal</p>
          </div>
        </div>
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          {items.map((m) => (
            <NavLink key={m.to} to={m.to} end={m.to === '/'} className={navLinkCls} data-testid={`nav-${m.label.toLowerCase().replace(' ', '-')}`}>
              <m.icon className="w-4.5 h-4.5 w-[18px] h-[18px]" />
              {m.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-line space-y-3">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate" data-testid="current-user-name">{user.name}</p>
              <BranchBadge branch={user.branch} />
            </div>
            <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} data-testid="theme-toggle"
              className="w-9 h-9 rounded-lg bg-elevated border border-line flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors">
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
          </div>
          <button onClick={handleLogout} data-testid="logout-button"
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-elevated border border-line text-sm font-medium text-muted-foreground hover:text-brand hover:border-brand/40 transition-colors">
            <LogOut className="w-4 h-4" /> Keluar
          </button>
        </div>
      </aside>

      <header className="lg:hidden sticky top-0 z-40 bg-surface/90 backdrop-blur-md border-b border-line">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-brand flex items-center justify-center">
              <Footprints className="w-5 h-5 text-white" />
            </div>
            <h1 className="font-display font-extrabold tracking-tight">LODISHOES</h1>
          </div>
          <div className="flex items-center gap-2">
            <BranchBadge branch={user.branch} />
            <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} data-testid="theme-toggle-mobile"
              className="w-8 h-8 rounded-lg bg-elevated border border-line flex items-center justify-center text-muted-foreground">
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button onClick={handleLogout} data-testid="logout-button-mobile"
              className="w-8 h-8 rounded-lg bg-elevated border border-line flex items-center justify-center text-muted-foreground hover:text-brand">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="lg:ml-64 px-4 sm:px-6 lg:px-8 py-5 pb-24 lg:pb-8">
        <Outlet />
      </main>

      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-surface/95 backdrop-blur-md border-t border-line">
        <div className="flex overflow-x-auto no-scrollbar px-2 py-2 gap-1">
          {items.map((m) => (
            <NavLink key={m.to} to={m.to} end={m.to === '/'} data-testid={`nav-mobile-${m.label.toLowerCase().replace(' ', '-')}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg min-w-[68px] text-[10px] font-medium transition-colors ${
                  isActive ? 'text-brand' : 'text-muted-foreground'
                }`
              }>
              <m.icon className="w-5 h-5" />
              {m.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

export function Protected() {
  const { user } = useAuth();
  if (user === undefined) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="loading-screen">
        <Loader2 className="w-8 h-8 text-brand animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Layout />;
}

export function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user?.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}
